import React from 'react';
import { LoadingButton } from '@mui/lab';
import { Grid2, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import usePurchaseTerminalReader from '../../../hooks/businesses/usePurchaseTerminalReader';
import TerminalProduct from './TerminalProduct';
import useGetTerminalProducts from '../../../hooks/businesses/useGetTerminalProducts';
import ColoredAlert from '../../global/ColoredAlert';
import useUser from '../../../hooks/global/useUser';
import PageLoading from '../../global/PageLoading';
import {
  isReactNativeWebView,
  postMessageToReactNative,
} from '../../../utils/global/helper';

function PurchaseTerminalSection() {
  const [params] = useSearchParams();
  const [selectedProduct, setSelectedProduct] = React.useState<string | null>(
    null,
  );

  const isSuccess = params.get('success') === 'true';

  const { user } = useUser();
  const { data: products } = useGetTerminalProducts();

  const isAlreadyPurchased =
    isSuccess ||
    !!user?.business?.isTerminalPurchased ||
    !!user?.business?.terminalReaderId;

  const { mutate: initiateCheckout, isPending } = usePurchaseTerminalReader({
    onSuccess: data => {
      if (!data.url) return;
      window.location.href = data.url;
    },
    onError: () => {
      toast.error(
        'Failed to initiate checkout. If the problem persists, please contact support.',
      );
    },
  });

  const onInitiateCheckout = () => {
    if (!selectedProduct) return;
    initiateCheckout(selectedProduct);
  };

  React.useEffect(() => {
    if (isAlreadyPurchased && isReactNativeWebView()) {
      postMessageToReactNative('terminal-purchased');
    }
  }, [isAlreadyPurchased]);

  if (!products) return <PageLoading />;

  return (
    <Stack width="100%" gap={2}>
      {(isSuccess || isAlreadyPurchased) && (
        <ColoredAlert
          dismissible={false}
          type="success"
          title={
            isSuccess
              ? 'Terminal purchase successful!'
              : 'Terminal already purchased!'
          }
          subtitle={
            isSuccess
              ? 'Your terminal will be delivered to the provided shipping address. If you have any questions, please contact support.'
              : 'Your terminal has already been purchased. If you have any questions, please contact support.'
          }
          containerSx={{
            width: '100%',
          }}
        />
      )}

      <Typography alignSelf="start" variant="h3">
        Select a product
      </Typography>

      <Stack
        width="100%"
        gap={{
          xs: 0,
          md: 2,
        }}
        alignItems="center"
        justifyContent="space-between">
        <Grid2 container columns={12} width="100%" spacing={2}>
          <TerminalProduct
            image={products.fullTerminal.image}
            title={products.fullTerminal.name}
            description={products.fullTerminal.description}
            price={
              (products.fullTerminal.prices.upfront.unit_amount || 0) / 100
            }
            monthlyPrice={
              (products.fullTerminal.prices.recurring.unit_amount || 0) / 100
            }
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            months={12}
            priceId={products.fullTerminal.prices.upfront.id}
            monthlyPriceId={products.fullTerminal.prices.recurring.id}
          />
          <TerminalProduct
            image={products.reader.image}
            title={products.reader.name}
            description={products.reader.description}
            price={(products.reader.prices.upfront.unit_amount || 0) / 100}
            monthlyPrice={
              (products.reader.prices.recurring.unit_amount || 0) / 100
            }
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            months={12}
            priceId={products.reader.prices.upfront.id}
            monthlyPriceId={products.reader.prices.recurring.id}
          />
        </Grid2>
        <Stack
          width="100%"
          bgcolor={theme => theme.palette.background.default}
          position={{
            xs: 'sticky',
            md: 'static',
          }}
          p={{
            xs: 2,
            md: 0,
          }}
          bottom={{
            xs: 0,
            md: 'auto',
          }}>
          <LoadingButton
            disabled={!selectedProduct || isAlreadyPurchased || isPending}
            loading={isPending}
            sx={{
              width: '100%',
            }}
            onClick={onInitiateCheckout}
            variant="base"
            color="primary">
            <Typography variant="inherit" fontWeight={600}>
              {isAlreadyPurchased
                ? 'Terminal already purchased'
                : selectedProduct
                ? 'Go to checkout'
                : 'Select a product'}
            </Typography>
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default PurchaseTerminalSection;
